import React, { useState, useEffect } from 'react';
import {
  Card, Steps, Form, Input, Select, InputNumber, Button, message,
  Row, Col, Typography, Space, Alert, Upload, Avatar,
} from 'antd';
import { CheckCircleOutlined, TeamOutlined, UploadOutlined, LoadingOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { absoluteUploadUrl } from '../../utils/uploadUrl';
import CepInput from '../../components/CepInput';

const { Title, Text } = Typography;

const SKILL_LEVELS = [
  { value: 'iniciante', label: 'Iniciante' },
  { value: 'recreativo', label: 'Recreativo (padrão)' },
  { value: 'intermediario', label: 'Intermediário' },
  { value: 'avancado', label: 'Avançado' },
  { value: 'semi_pro', label: 'Semi-profissional' },
];

const DAYS = [
  { value: 0, label: 'Dom' },
  { value: 1, label: 'Seg' },
  { value: 2, label: 'Ter' },
  { value: 3, label: 'Qua' },
  { value: 4, label: 'Qui' },
  { value: 5, label: 'Sex' },
  { value: 6, label: 'Sáb' },
];

const HORARIOS_COMUNS = ['18:00', '18:30', '19:00', '19:30', '20:00', '20:30', '21:00', '21:30', '08:00', '09:00', '10:00'];

export default function TeamOwnerSetup() {
  const [step, setStep] = useState(0);
  const [payload, setPayload] = useState({});
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [existingTeam, setExistingTeam] = useState(null);
  const [form1] = Form.useForm();
  const [form2] = Form.useForm();
  const [form3] = Form.useForm();
  const nav = useNavigate();

  useEffect(() => {
    // Se player logado ja tem time-comunidade, redireciona pro dashboard
    (async () => {
      try {
        const r = await api.get('/community/team-owner/my');
        if (r.data.teams && r.data.teams.length > 0) {
          setExistingTeam(r.data.teams[0]);
        }
      } catch { /* sem time ainda */ }
    })();
  }, []);

  const next1 = async () => {
    try {
      const vals = await form1.validateFields();
      setPayload(p => ({ ...p, ...vals }));
      setStep(1);
    } catch {}
  };
  const next2 = async () => {
    try {
      const vals = await form2.validateFields();
      setPayload(p => ({ ...p, ...vals }));
      setStep(2);
    } catch {}
  };
  const finish = async () => {
    try {
      const vals = await form3.validateFields();
      const finalPayload = { ...payload, ...vals };
      setLoading(true);
      const r = await api.post('/community/team-owner/setup', finalPayload);
      message.success('Time criado! Já tá no mapa.');
      nav(`/time/dashboard/${r.data.team.id}`);
    } catch (err) {
      message.error(err.response?.data?.message || 'Erro ao criar time');
    } finally { setLoading(false); }
  };

  if (existingTeam) {
    return (
      <div style={{ maxWidth: 700, margin: '40px auto', padding: 24 }}>
        <Card>
          <Space direction="vertical" size="large" style={{ width: '100%', textAlign: 'center' }}>
            <CheckCircleOutlined style={{ fontSize: 48, color: '#52c41a' }} />
            <div>
              <Title level={4} style={{ margin: 0 }}>Você já tem um time cadastrado</Title>
              <Text type="secondary">{existingTeam.name}</Text>
            </div>
            <Button type="primary" size="large" onClick={() => nav(`/time/dashboard/${existingTeam.id}`)}>
              Ir pro painel do time
            </Button>
          </Space>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 720, margin: '20px auto', padding: 16 }}>
      <Card>
        <Title level={4} style={{ margin: 0 }}>
          <TeamOutlined /> Cadastro do time na comunidade
        </Title>
        <Text type="secondary">Aparece no mapa pra jogadores solicitarem entrada. Grátis.</Text>

        <Steps
          current={step}
          style={{ marginTop: 20, marginBottom: 24 }}
          items={[
            { title: 'Identidade' },
            { title: 'Quadra' },
            { title: 'Jogos & valores' },
          ]}
        />

        {step === 0 && (
          <Form form={form1} layout="vertical" initialValues={payload}>
            <Form.Item label="Nome do time" name="name"
              rules={[{ required: true, min: 3, message: 'Mín 3 caracteres' }]}>
              <Input placeholder="Ex: Racha da Sexta FC" />
            </Form.Item>
            <Form.Item label="Descrição (opcional)" name="description">
              <Input.TextArea rows={3} maxLength={500} showCount
                placeholder="Conta um pouco sobre o time — vibe, quem joga, o que buscam..." />
            </Form.Item>
            <Form.Item label="Logo do time (opcional)" name="logo_url"
              extra="PNG, JPG ou WebP. Máx 2MB. Fica visível no mapa e no perfil.">
              <div>
                <Upload
                  accept="image/png,image/jpeg,image/webp"
                  showUploadList={false}
                  customRequest={async ({ file, onSuccess, onError }) => {
                    setUploading(true);
                    try {
                      const fd = new FormData();
                      fd.append('image', file);
                      const r = await api.post('/community/uploads/image', fd, {
                        headers: { 'Content-Type': 'multipart/form-data' },
                      });
                      setLogoUrl(r.data.url);
                      form1.setFieldsValue({ logo_url: r.data.url });
                      onSuccess(r.data);
                      message.success('Logo enviada');
                    } catch (err) {
                      onError(err);
                      message.error(err.response?.data?.message || 'Erro ao enviar');
                    } finally { setUploading(false); }
                  }}
                >
                  <Space>
                    {logoUrl && <Avatar size={64} src={absoluteUploadUrl(logoUrl)} />}
                    <Button icon={uploading ? <LoadingOutlined /> : <UploadOutlined />} loading={uploading}>
                      {logoUrl ? 'Trocar logo' : 'Escolher arquivo'}
                    </Button>
                  </Space>
                </Upload>
              </div>
            </Form.Item>
            <Form.Item name="logo_url" hidden><Input /></Form.Item>
            <Form.Item label="Nível dos jogadores" name="skill_level" initialValue="recreativo">
              <Select options={SKILL_LEVELS} />
            </Form.Item>
            <Button type="primary" size="large" block onClick={next1}>Continuar</Button>
          </Form>
        )}

        {step === 1 && (
          <Form form={form2} layout="vertical" initialValues={payload}>
            <Alert type="info" showIcon style={{ marginBottom: 16 }}
              message="Coordenadas serão salvas do CEP pra aparecer no mapa. O endereço exato só aparece pra jogadores que confirmarem interesse." />
            <Form.Item label="Nome da quadra (opcional)" name="court_name">
              <Input placeholder="Ex: Arena Sport, Campo do Bairro..." />
            </Form.Item>
            <Row gutter={12}>
              <Col xs={24} md={10}>
                <Form.Item label="CEP" name="cep" rules={[{ required: true, message: 'CEP obrigatório' }]}>
                  <CepInput onAddressChange={(g) => {
                    if (g) {
                      form2.setFieldsValue({
                        address: g.address || undefined,
                        city: g.city || undefined,
                        state: g.state || undefined,
                        neighborhood: g.neighborhood || undefined,
                        latitude: g.latitude || undefined,
                        longitude: g.longitude || undefined,
                      });
                    }
                  }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={14}>
                <Form.Item label="Endereço" name="address" rules={[{ required: true }]}>
                  <Input />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={12}>
              <Col xs={12} md={8}><Form.Item label="Cidade" name="city" rules={[{ required: true }]}><Input /></Form.Item></Col>
              <Col xs={12} md={4}><Form.Item label="UF" name="state" rules={[{ required: true }]}><Input maxLength={2} /></Form.Item></Col>
              <Col xs={24} md={12}><Form.Item label="Bairro" name="neighborhood"><Input /></Form.Item></Col>
            </Row>
            <Form.Item name="latitude" hidden><Input /></Form.Item>
            <Form.Item name="longitude" hidden><Input /></Form.Item>
            <Space>
              <Button onClick={() => setStep(0)}>Voltar</Button>
              <Button type="primary" onClick={next2}>Continuar</Button>
            </Space>
          </Form>
        )}

        {step === 2 && (
          <Form form={form3} layout="vertical" initialValues={payload}>
            <Form.Item label="Dias de jogo" name="days_of_week"
              rules={[{ required: true, message: 'Selecione pelo menos 1 dia' }]}>
              <Select mode="multiple" options={DAYS} placeholder="Ex: Terça, Quinta" />
            </Form.Item>
            <Form.Item label="Horários dos jogos" name="game_times"
              rules={[{ required: true, message: 'Adicione pelo menos 1 horário' }]}>
              <Select mode="tags" placeholder="Ex: 19:00, 20:30"
                options={HORARIOS_COMUNS.map(h => ({ value: h, label: h }))} />
            </Form.Item>
            <Row gutter={12}>
              <Col xs={12}>
                <Form.Item label="Valor avulso (R$)" name="guest_price">
                  <InputNumber min={0} step={5} style={{ width: '100%' }} placeholder="30" />
                </Form.Item>
              </Col>
              <Col xs={12}>
                <Form.Item label="Mensalidade (R$)" name="monthly_price">
                  <InputNumber min={0} step={10} style={{ width: '100%' }} placeholder="100" />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item label="Raio de busca de jogadores (km)" name="search_radius_km" initialValue={15}>
              <InputNumber min={1} max={100} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item label="Código de conduta" name="conduct_code"
              initialValue="Respeito mútuo, fair play, pontualidade, sem violência.">
              <Input.TextArea rows={3} maxLength={500} showCount />
            </Form.Item>
            <Space>
              <Button onClick={() => setStep(1)}>Voltar</Button>
              <Button type="primary" size="large" loading={loading} onClick={finish}>
                Criar time e publicar no mapa
              </Button>
            </Space>
            <div style={{ marginTop: 16, fontSize: 12, color: '#8a9a93' }}>
              Ao cadastrar você concorda com os{' '}
              <a href="/termos" target="_blank" rel="noreferrer" style={{ color: '#128C7E' }}>termos de uso</a>.
            </div>
          </Form>
        )}
      </Card>
    </div>
  );
}
